import { Form, useForm, useFormContext } from "@/components/form";
import { Stack, StackProps, Typography } from "@mui/material";
import { z } from "zod/v4";
import { forwardRef, useImperativeHandle, useMemo } from "react";
import { UNIDADES_MEDIDA } from "../../utils/unidades-medida";
import { FormFieldWithError } from "@/components/form/form-field-with-error";
import { LENGTH_CDC } from "@/shared/types/documento-electronico";

const DEFAULT_IVA = "10";

interface DEArticuloSearchProps extends StackProps {
    onArticuloSelect?: (value: DetalleDE) => void
    disabled?: boolean
    forAutoFactura?: boolean
    hidePrecio?: boolean
    isB2G?: boolean
    isAnticipo?: boolean
}

export interface DetalleDE {
    codigo: string;
    descripcion: string;
    cantidad: number;
    precio: number;
    iva?: "exenta" | "5" | "10";
    unidadMedida: number;
    dDncpG?: string;
    dDncpE?: string;
    cdcAnticipo?: string;
}

const deArticuloSearchFormSchemaBase = z.object({
    codigo: z.string().nonempty("Este campo es requerido"),
    descripcion: z.string().nonempty("Este campo es requerido"),
    cantidad: z.number({ message: "Este campo es requerido" }).min(1, "El valor debe ser mayor a 0"),
    precio: z.number({ message: "Este campo es requerido" }).min(0, "El valor debe ser mayor o igual a 0"),
    iva: z.enum(["exenta", "5", "10"]).optional(),
    unidadMedida: z.number({ message: "Este campo es requerido" }).min(1, "Debe seleccionar una unidad de medida"),
    dDncpG: z.string().optional(),
    dDncpE: z.string().optional(),
    cdcAnticipo: z.string().optional(),
});

const deArticuloSearchFormSchemaRequiredIva = deArticuloSearchFormSchemaBase.extend({
    iva: z.enum(["exenta", "5", "10"])
});

const deArticuloSearchFormSchemaB2G = deArticuloSearchFormSchemaRequiredIva.extend({
    dDncpG: z.string().optional(),
    dDncpE: z.string().optional(),
});

const deArticuloSearchFormSchemaAnticipo = deArticuloSearchFormSchemaRequiredIva.extend({
    cdcAnticipo: z.string().optional().refine(
        (val) => !val || val.length === LENGTH_CDC,
        { message: `El CDC debe tener ${LENGTH_CDC} caracteres` }
    ),
});

const DEArticuloSearch = forwardRef<any, DEArticuloSearchProps>(function DEArticuloSearch({
    onArticuloSelect,
    disabled,
    forAutoFactura,
    hidePrecio,
    isB2G,
    isAnticipo,
    ...props
}, ref) {
    const parentForm = useFormContext();
    const validatorSchema = useMemo(() => {
        if (isAnticipo) return deArticuloSearchFormSchemaAnticipo;
        if (isB2G) return deArticuloSearchFormSchemaB2G;
        if (forAutoFactura || hidePrecio) return deArticuloSearchFormSchemaBase;
        return deArticuloSearchFormSchemaRequiredIva;
    }, [forAutoFactura, hidePrecio, isB2G, isAnticipo]);
    const form = useForm({
        defaultValues: {
            codigo: "",
            descripcion: "",
            cantidad: 1,
            precio: 0,
            iva: forAutoFactura ? undefined : DEFAULT_IVA,
            unidadMedida: 77, // Default es Unidad
            dDncpG: "",
            dDncpE: "",
            cdcAnticipo: "",
        } as z.infer<typeof deArticuloSearchFormSchemaBase>,
        submitOnce: false,
        closeModalOnSubmit: false,
        validators: {
            onSubmit: validatorSchema
        },
        enterBehaviour: "selectNext",
        onSubmit: ({ value, formApi }) => {
            onArticuloSelect && onArticuloSelect({
                codigo: value.codigo,
                descripcion: value.descripcion,
                cantidad: value.cantidad,
                precio: value.precio,
                iva: value.iva,
                unidadMedida: value.unidadMedida,
                dDncpG: value.dDncpG || undefined,
                dDncpE: value.dDncpE || undefined,
                cdcAnticipo: value.cdcAnticipo || undefined,
            });

            formApi.reset();
            formApi.setFieldValue("codigo", "");
            formApi.setFieldValue("descripcion", "");
            formApi.setFieldValue("cantidad", 1);
            formApi.setFieldValue("precio", 0);
            formApi.setFieldValue("iva", forAutoFactura ? undefined : DEFAULT_IVA);
            formApi.setFieldValue("unidadMedida", 77);
            formApi.setFieldValue("dDncpG", "");
            formApi.setFieldValue("dDncpE", "");
            formApi.setFieldValue("cdcAnticipo", "");
        }
    });

    useImperativeHandle(ref, () => ({
        focus: () => {
            const firstInput = document.querySelector('input[name="codigo"]') as HTMLInputElement;
            if (firstInput) {
                firstInput.focus();
            }
        }
    }));

    return <Stack gap={1.5} {...props}>
        <Typography variant="h6" color="primary" sx={{ mb: 0.5 }}>Agregar Ítem</Typography>

        <Form form={form} direction={"column"} gap={1.5}>
            <FormFieldWithError
                name="codigo"
                label="Código del ítem *"
                size="small"
                disabled={disabled}
                heightError="50px"
            />

            <FormFieldWithError
                name="descripcion"
                label="Descripción del producto/servicio *"
                size="small"
                disabled={disabled}
                heightError="50px"
            />

            <Stack direction="row" gap={1.5}>
                <FormFieldWithError
                    name="cantidad"
                    label="Cantidad *"
                    size="small"
                    sx={{ flex: 1 }}
                    disabled={disabled}
                    fieldKind="number"
                    heightError="70px"
                />
                <Form.Select
                    name="unidadMedida"
                    label="Unidad de Medida *"
                    size="small"
                    sx={{ flex: 2 }}
                    disabled={disabled}
                >
                    {UNIDADES_MEDIDA.map(u => (
                        <Form.Select.Option key={u.codigo} value={u.codigo}>
                            {u.descripcion} ({u.representacion})
                        </Form.Select.Option>
                    ))}
                </Form.Select>
            </Stack>

            {!hidePrecio && <FormFieldWithError
                fieldKind="moneda"
                name="precio"
                label="Precio Unitario *"
                size="small"
                disabled={disabled}
            />}

            {!forAutoFactura && <Form.RadioGroup
                name="iva"
                label="IVA *"
                row
            >
                <Form.RadioButton value="exenta" label="Exenta" disabled={disabled} />
                <Form.RadioButton value="5" label="5%" disabled={disabled} />
                <Form.RadioButton value="10" label="10%" disabled={disabled} />
            </Form.RadioGroup>}

            {isB2G && <>
                <FormFieldWithError
                    name="dDncpG"
                    label="Código DNCP General"
                    size="small"
                    disabled={disabled}
                    heightError="50px"
                    slotProps={{ htmlInput: { maxLength: 8 } }}
                />
                <FormFieldWithError
                    name="dDncpE"
                    label="Código DNCP Específico"
                    size="small"
                    disabled={disabled}
                    heightError="50px"
                    slotProps={{ htmlInput: { maxLength: 4 } }}
                />
            </>}

            {isAnticipo && <FormFieldWithError
                name="cdcAnticipo"
                label="CDC de la Factura de Anticipo"
                size="small"
                disabled={disabled}
                heightError="50px"
                placeholder="00000000000000000000000000000000000000000000"
                slotProps={{ htmlInput: { maxLength: LENGTH_CDC } }}
            />}

            <Form.SubmitButton variant="contained" size="small" disabled={disabled}>
                Agregar Ítem
            </Form.SubmitButton>
        </Form>

        <parentForm.Field name="items" mode="array">
            {field => field.state.meta.errors && field.state.meta.errors.length > 0 && (
                <Stack sx={{ color: 'error.main', fontSize: '0.75rem', mt: 1 }}>
                    {field.state.meta.errors.map((error: any, index: number) => (
                        <div key={index}>{typeof error === 'string' ? error : error.message || 'Error de validación'}</div>
                    ))}
                </Stack>
            )}
        </parentForm.Field>
    </Stack>
});

export default DEArticuloSearch;
